import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Copy, Check, ExternalLink, ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBitcoinPrice, usdToSats, formatSats } from '@/hooks/useBitcoinPrice';
import { genUserName } from '@/lib/genUserName';
import type { NostrEvent } from '@nostrify/nostrify';
import QRCode from 'qrcode';

interface SupportDialogProps {
  pubkey: string;
  event: NostrEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const presetAmounts = [1, 3, 5, 10, 20];
const defaultAmount = 5;

export function SupportDialog({ pubkey, event, open, onOpenChange }: SupportDialogProps) {
  const { user } = useCurrentUser();
  const { data: authorData } = useAuthor(pubkey);
  const { toast } = useToast();
  const { webln, activeNWC, hasWebLN, detectWebLN } = useWallet();
  const { zap, isZapping, invoice, setInvoice } = useZaps(event, webln, activeNWC, () => onOpenChange(false));
  const { data: btcPrice, isLoading: isPriceLoading } = useBitcoinPrice();
  const isMobile = useIsMobile();

  const [selectedUsd, setSelectedUsd] = useState(defaultAmount);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  const metadata = authorData?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const hasLightningAddress = !!(metadata?.lud06 || metadata?.lud16);

  // Calculate sats from USD
  const satsAmount = btcPrice ? usdToSats(selectedUsd, btcPrice) : 0;

  // Detect WebLN when dialog opens
  useEffect(() => {
    if (open && !hasWebLN) {
      detectWebLN();
    }
  }, [open, hasWebLN, detectWebLN]);

  // Generate QR code
  useEffect(() => {
    let isCancelled = false;

    const generateQR = async () => {
      if (!invoice) {
        setQrCodeUrl('');
        return;
      }

      try {
        const url = await QRCode.toDataURL(invoice.toUpperCase(), {
          width: 512,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });

        if (!isCancelled) {
          setQrCodeUrl(url);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to generate QR code:', err);
        }
      }
    };

    generateQR();

    return () => {
      isCancelled = true;
    };
  }, [invoice]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUsd(defaultAmount);
      setComment('');
      setInvoice(null);
      setCopied(false);
      setQrCodeUrl('');
    }
  }, [open, setInvoice]);

  const handleCopy = useCallback(async () => {
    if (invoice) {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      toast({
        title: 'Invoice copied',
        description: 'Lightning invoice copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  }, [invoice, toast]);

  const openInWallet = useCallback(() => {
    if (invoice) {
      const lightningUrl = `lightning:${invoice}`;
      window.open(lightningUrl, '_blank');
    }
  }, [invoice]);

  const handleContinue = useCallback(() => {
    if (satsAmount > 0) {
      zap(satsAmount, comment);
    }
  }, [satsAmount, comment, zap]);

  const handleBack = useCallback(() => {
    setInvoice(null);
    setQrCodeUrl('');
  }, [setInvoice]);

  // Form Content (amount selection)
  const FormContent = (
    <div className="flex flex-col">
      {/* Title */}
      <div className="text-center py-4 px-6">
        <h2 className="text-xl font-semibold">Support {displayName}</h2>
      </div>

      {/* Large USD display */}
      <div className="flex items-center justify-center gap-2 py-6">
        <span className="text-5xl font-light text-muted-foreground">$</span>
        <motion.div
          key={selectedUsd}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="bg-primary/20 rounded-lg px-4 py-2"
        >
          <span className="text-5xl font-bold text-primary">{selectedUsd}</span>
        </motion.div>
      </div>

      {/* Sats equivalent */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-muted/80 rounded-full px-4 py-2">
          <Zap className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium">
            {isPriceLoading ? '...' : `${formatSats(satsAmount)} sats`}
          </span>
        </div>
      </div>

      {/* Amount selection buttons */}
      <div className="grid grid-cols-5 gap-2 px-6 mb-6">
        {presetAmounts.map((amount) => (
          <button
            key={amount}
            onClick={() => setSelectedUsd(amount)}
            className={cn(
              'relative py-4 px-2 rounded-xl border-2 transition-all duration-200',
              'hover:border-primary/50 hover:bg-primary/5',
              'focus:outline-none focus:ring-2 focus:ring-primary/30',
              selectedUsd === amount
                ? 'border-primary bg-primary text-primary-foreground font-semibold'
                : 'border-border bg-background'
            )}
          >
            <span className="text-lg font-medium">${amount}</span>
          </button>
        ))}
      </div>

      {/* Optional message */}
      <div className="px-6 mb-6">
        <Textarea
          placeholder="Add a message (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="resize-none rounded-xl border-border/60 bg-muted/30"
          rows={3}
        />
      </div>

      {/* Continue button */}
      <div className="px-6 pb-6">
        <Button
          onClick={handleContinue}
          disabled={isZapping || !hasLightningAddress || satsAmount === 0}
          className="w-full h-14 rounded-xl text-lg font-semibold"
          size="lg"
        >
          {isZapping ? 'Creating invoice...' : 'Continue'}
        </Button>
      </div>
    </div>
  );

  // Payment Content (QR code and invoice)
  const PaymentContent = (
    <div className="flex flex-col">
      {/* Header with back button */}
      <div className="flex items-center justify-between p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="text-center flex-1">
          <h2 className="text-lg font-semibold">Lightning Payment</h2>
          <p className="text-sm text-muted-foreground">{formatSats(satsAmount)} sats (${selectedUsd})</p>
        </div>
        <div className="w-16" /> {/* Spacer for centering */}
      </div>

      {/* QR Code */}
      <div className="flex justify-center px-6 py-4">
        <Card className="p-3 max-w-[280px] w-full">
          <CardContent className="p-0 flex justify-center">
            {qrCodeUrl ? (
              <img
                src={qrCodeUrl}
                alt="Lightning Invoice QR Code"
                className="w-full h-auto aspect-square object-contain rounded"
              />
            ) : (
              <div className="w-full aspect-square bg-muted animate-pulse rounded" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice input */}
      <div className="space-y-2 px-6 mb-4">
        <Label htmlFor="invoice" className="text-sm text-muted-foreground">Lightning Invoice</Label>
        <div className="flex gap-2">
          <Input
            id="invoice"
            value={invoice || ''}
            readOnly
            className="font-mono text-xs overflow-hidden text-ellipsis"
            onClick={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopy}
            className="shrink-0"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Payment buttons */}
      <div className="space-y-3 px-6 pb-6">
        {hasWebLN && (
          <Button
            onClick={() => zap(satsAmount, comment)}
            disabled={isZapping}
            className="w-full h-12 rounded-xl"
            size="lg"
          >
            <Zap className="h-4 w-4 mr-2" />
            {isZapping ? 'Processing...' : 'Pay with WebLN'}
          </Button>
        )}

        <Button
          variant="outline"
          onClick={openInWallet}
          className="w-full h-12 rounded-xl"
          size="lg"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open in Lightning Wallet
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Scan the QR code or copy the invoice to pay with any Lightning wallet.
        </p>
      </div>
    </div>
  );

  // Desktop Dialog
  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden bg-card">
          <DialogTitle className="sr-only">Support {displayName}</DialogTitle>
          <AnimatePresence mode="wait">
            {invoice ? (
              <motion.div
                key="payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {PaymentContent}
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                {FormContent}
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile Drawer
  return (
    <Drawer
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          setInvoice(null);
          setQrCodeUrl('');
        }
        onOpenChange(newOpen);
      }}
      dismissible={true}
    >
      <DrawerContent className="max-h-[95vh]">
        <DrawerTitle className="sr-only">Support {displayName}</DrawerTitle>
        <DrawerClose asChild>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 z-10"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DrawerClose>
        <div className="overflow-y-auto">
          <AnimatePresence mode="wait">
            {invoice ? (
              <motion.div
                key="payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {PaymentContent}
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                {FormContent}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
